import { Link, RouteComponentProps } from '@reach/router';
import { LessonsApi, Unit, UnitLesson } from 'api/lessonsApi';
import LessonLandingTop from 'components/lessons/LessonLandingTop';
import LessonPageStatus from 'components/lessons/LessonPageStatus';
import { useLessonPrint } from 'components/lessons/LessonPrint';
import {
  AccessButton,
  closedAccessButton,
  LessonsRouteParams,
  openAccessButton,
  openFileInNewTab,
  SUBSCRIBERS_ONLY_BUTTON,
  unitPath,
  useLessonsPage,
} from 'components/lessons/lessonsHelpers';
import SubjectSiteLayout from 'components/subjectSite/SubjectSiteLayout';
import ProgressImage from 'images/subjectSite/progress-indicator-6.gif';
import React, { useState } from 'react';

type Props = RouteComponentProps<LessonsRouteParams>;

type CardLines = {
  /**
   * Txt_SW or Txt_LP
   */
  list: 'SW' | 'LP';
  details: (string | null)[];
};

const LessonUnit: React.FC<Props> = (props) => {
  const result = useLessonsPage<Unit>(props, (query) =>
    LessonsApi.getUnit(query)
  );
  const subjectsId =
    result && typeof result !== 'string' ? result.subject.subjectsID : null;
  const { checkQuota, popup, bError } = useLessonPrint(subjectsId);
  const [bFileError, setBFileError] = useState(false);
  const [bChecking, setBChecking] = useState(false);

  if (result === null || typeof result === 'string') {
    return <LessonPageStatus status={result} />;
  }

  const { subject, query, details: unit } = result;
  const { activity, keyStageName } = unit;
  const path = unitPath(
    subject.internalCode,
    unit.keyStage.strippedName,
    activity.strippedName
  );
  const viewText = unit.access.bFullAccess ? 'View' : 'View Sample';

  const lessonButton = (bCanOpen: boolean, href: string): AccessButton => {
    if (bCanOpen) return openAccessButton(viewText, href);
    return unit.access.bFullAccess
      ? SUBSCRIBERS_ONLY_BUTTON
      : closedAccessButton(unit.access);
  };

  /**
   * Without full access only a featured sample says View Sample, and it links only when it opens
   */
  const unitLessonButton = (lesson: UnitLesson, href: string): AccessButton => {
    if (unit.access.bFullAccess) return lessonButton(lesson.bCanOpen, href);
    return lesson.bFeaturedSample
      ? openAccessButton(viewText, lesson.bCanOpen ? href : null)
      : closedAccessButton(unit.access);
  };

  const cardContent = ({ list, details }: CardLines, button: AccessButton) => (
    <span className={`Table_${list}`}>
      <span className={`Year_${list}`}>
        <span className={`Time_${list}`}>{keyStageName}</span>
      </span>
      <span className={`Desc_${list}`}>
        <span className={`Txt_${list}`}>{activity.name}</span>
        {details.map(
          (detail, index) =>
            detail && (
              <span key={index} className={`Nbre_${list}`}>
                {detail}
              </span>
            )
        )}
      </span>
      <span className={`Button_${list}`}>
        <span className={button.className}>
          <span>{button.text}</span>
        </span>
      </span>
    </span>
  );

  const card = (
    key: string | number,
    lines: CardLines,
    button: AccessButton
  ) => {
    const className = `Item_${lines.list}${
      button.bDisabled ? ' Disabled' : ''
    }`;
    const content = cardContent(lines, button);

    return button.href ? (
      <Link key={key} to={button.href} className={className}>
        {content}
      </Link>
    ) : (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <a key={key} className={className}>
        {content}
      </a>
    );
  };

  const openKnowledgeOrganiser = (resourcesId: number) => {
    setBFileError(false);
    setBChecking(true);
    let fileWindow = window.open('', '_blank');
    const getUrl = () =>
      LessonsApi.getResourceFileUrl(subject.subjectsID, resourcesId, false);
    checkQuota(
      `${window.location.pathname}/knowledge-organiser`,
      () => {
        setBChecking(false);
        openFileInNewTab(getUrl, fileWindow ?? window.open('', '_blank')).then(
          (bOpened) => setBFileError(!bOpened)
        );
        fileWindow = null;
      },
      () => {
        setBChecking(false);
        fileWindow?.close();
        fileWindow = null;
      }
    );
  };

  const scheme = unit.schemeOfWork;
  const knowledgeOrganiser = unit.knowledgeOrganiser;
  const knowledgeLines: CardLines = {
    list: 'SW',
    details: ['Knowledge Organiser'],
  };

  return (
    <SubjectSiteLayout colorCss={activity.colorCSS}>
      <div className="LessonLanding">
        <LessonLandingTop activity={activity} keyStageName={keyStageName} />

        <div className="Panel_One" id="Panel_One">
          <div className="Inner">
            <div className="WidthContent">
              {scheme ? (
                <>
                  <div id="KnowledgeSchemeError">
                    {(bError || bFileError) && (
                      <span>An error occurred, please try again.</span>
                    )}
                  </div>
                  <div className="WarpperShemeWork">
                    {card(
                      'scheme',
                      { list: 'SW', details: [scheme.name] },
                      lessonButton(
                        scheme.bCanOpen,
                        `${path}/schemeofwork?years=${query.years}`
                      )
                    )}
                    {knowledgeOrganiser &&
                      (scheme.bCanOpen ? (
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                        <a
                          href="#"
                          className="Item_SW KnowledgeBlock"
                          onClick={(event) => {
                            event.preventDefault();
                            if (!bChecking)
                              openKnowledgeOrganiser(
                                knowledgeOrganiser.resourcesId
                              );
                          }}
                        >
                          {bChecking && (
                            <span className="LoadingBlock">
                              <span className="Inner">
                                <img src={ProgressImage} alt="Loading" />
                              </span>
                            </span>
                          )}
                          {cardContent(
                            knowledgeLines,
                            openAccessButton('View', null)
                          )}
                        </a>
                      ) : (
                        card(
                          'knowledge',
                          knowledgeLines,
                          lessonButton(false, '')
                        )
                      ))}
                  </div>
                </>
              ) : (
                <div className="WarpperShemeWork">
                  <p>No Unit of Work details found</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="Panel_Two">
          <div className="Inner">
            <div className="WidthContent">
              <h2 className="Subtitle">Lesson Plans</h2>
              <div className="WarpperLessonPlans">
                {unit.lessons.length === 0 ? (
                  <p>No Lesson plans available for this activity</p>
                ) : (
                  unit.lessons.map((lesson) =>
                    card(
                      lesson.lessonsId,
                      {
                        list: 'LP',
                        details: [
                          `Lesson ${lesson.lessonNumber}`,
                          lesson.theme,
                        ],
                      },
                      unitLessonButton(
                        lesson,
                        `${path}/${lesson.lessonNumber}?years=${query.years}`
                      )
                    )
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {popup}
    </SubjectSiteLayout>
  );
};

export default LessonUnit;
